import {
  HttpClient,
  HttpHeaders,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { apiConfiguration } from '../../../../configurations/api-configuration';
import { AccessService } from '../../../application/services/AccessService';
import { HttpResponse } from '../../../application/types/HttpResponse';
import {
  MailingAudiencePreview,
  MailingCampaign,
  MailingImageUploadResult,
  MailingWorkerRunResult,
  MessagingContext,
  SaveMailingCampaignData,
  SaveTelegramBotData,
  SaveTelegramParserData,
  SaveTelegramAccountData,
  TelegramAccount,
  TelegramBot,
  TelegramConfirmCodeResult,
  TelegramParser,
  TelegramSendCodeResult,
} from '../../types/Messaging';

@Injectable()
export class MessagingApiService {
  private readonly telegramUrl = `${apiConfiguration.baseApiUrl}/telegram`;
  private readonly mailingsUrl = `${apiConfiguration.baseApiUrl}/mailings`;

  public constructor(
    private http: HttpClient,
    private accessService: AccessService,
  ) { }

  public getContext(): Promise<HttpResponse<MessagingContext>> {
    return lastValueFrom(this.http.get<HttpResponse<MessagingContext>>(`${this.telegramUrl}/context`, { headers: this.getHeaders() }));
  }

  public getMailings(): Promise<HttpResponse<MailingCampaign[]>> {
    return lastValueFrom(this.http.get<HttpResponse<MailingCampaign[]>>(this.mailingsUrl, { headers: this.getHeaders() }));
  }

  public getMailing(mailingId: string): Promise<HttpResponse<MailingCampaign | null>> {
    return lastValueFrom(this.http.get<HttpResponse<MailingCampaign | null>>(`${this.mailingsUrl}/${mailingId}`, { headers: this.getHeaders() }));
  }

  public previewMailing(data: SaveMailingCampaignData): Promise<HttpResponse<MailingAudiencePreview>> {
    return lastValueFrom(this.http.post<HttpResponse<MailingAudiencePreview>>(`${this.mailingsUrl}/preview`, data, { headers: this.getHeaders() }));
  }

  public createMailing(data: SaveMailingCampaignData): Promise<HttpResponse<MailingCampaign>> {
    return lastValueFrom(this.http.post<HttpResponse<MailingCampaign>>(this.mailingsUrl, data, { headers: this.getHeaders() }));
  }

  public updateMailing(mailingId: string, data: SaveMailingCampaignData): Promise<HttpResponse<MailingCampaign>> {
    return lastValueFrom(this.http.put<HttpResponse<MailingCampaign>>(`${this.mailingsUrl}/${mailingId}`, data, { headers: this.getHeaders() }));
  }

  public deleteMailing(mailingId: string): Promise<HttpResponse<void>> {
    return lastValueFrom(this.http.delete<HttpResponse<void>>(`${this.mailingsUrl}/${mailingId}`, { headers: this.getHeaders() }));
  }

  public uploadMailingImage(file: File): Promise<HttpResponse<MailingImageUploadResult>> {
    const formData = new FormData();
    formData.append('image', file);
    return lastValueFrom(this.http.post<HttpResponse<MailingImageUploadResult>>(`${this.mailingsUrl}/image`, formData, { headers: this.getHeaders() }));
  }

  public runMailingDispatchQueue(): Promise<HttpResponse<MailingWorkerRunResult>> {
    return lastValueFrom(this.http.post<HttpResponse<MailingWorkerRunResult>>(`${this.mailingsUrl}/dispatch/run`, {}, { headers: this.getHeaders() }));
  }

  public createTelegramAccount(data: SaveTelegramAccountData): Promise<HttpResponse<TelegramAccount>> {
    return lastValueFrom(this.http.post<HttpResponse<TelegramAccount>>(`${this.telegramUrl}/accounts`, data, { headers: this.getHeaders() }));
  }

  public updateTelegramAccount(accountId: string, data: Partial<SaveTelegramAccountData>): Promise<HttpResponse<TelegramAccount>> {
    return lastValueFrom(this.http.put<HttpResponse<TelegramAccount>>(`${this.telegramUrl}/accounts/${accountId}`, data, { headers: this.getHeaders() }));
  }

  public deleteTelegramAccount(accountId: string): Promise<HttpResponse<void>> {
    return lastValueFrom(this.http.delete<HttpResponse<void>>(`${this.telegramUrl}/accounts/${accountId}`, { headers: this.getHeaders() }));
  }

  public createTelegramBot(data: SaveTelegramBotData): Promise<HttpResponse<TelegramBot>> {
    return lastValueFrom(this.http.post<HttpResponse<TelegramBot>>(`${this.telegramUrl}/bots`, data, { headers: this.getHeaders() }));
  }

  public updateTelegramBot(botId: string, data: Partial<SaveTelegramBotData>): Promise<HttpResponse<TelegramBot>> {
    return lastValueFrom(this.http.put<HttpResponse<TelegramBot>>(`${this.telegramUrl}/bots/${botId}`, data, { headers: this.getHeaders() }));
  }

  public deleteTelegramBot(botId: string): Promise<HttpResponse<void>> {
    return lastValueFrom(this.http.delete<HttpResponse<void>>(`${this.telegramUrl}/bots/${botId}`, { headers: this.getHeaders() }));
  }

  public createTelegramParser(data: SaveTelegramParserData): Promise<HttpResponse<TelegramParser>> {
    return lastValueFrom(this.http.post<HttpResponse<TelegramParser>>(`${this.telegramUrl}/parsers`, data, { headers: this.getHeaders() }));
  }

  public updateTelegramParser(parserId: string, data: Partial<SaveTelegramParserData>): Promise<HttpResponse<TelegramParser>> {
    return lastValueFrom(this.http.put<HttpResponse<TelegramParser>>(`${this.telegramUrl}/parsers/${parserId}`, data, { headers: this.getHeaders() }));
  }

  public startTelegramParser(parserId: string): Promise<HttpResponse<TelegramParser>> {
    return lastValueFrom(this.http.post<HttpResponse<TelegramParser>>(`${this.telegramUrl}/parsers/${parserId}/start`, {}, { headers: this.getHeaders() }));
  }

  public stopTelegramParser(parserId: string): Promise<HttpResponse<TelegramParser>> {
    return lastValueFrom(this.http.post<HttpResponse<TelegramParser>>(`${this.telegramUrl}/parsers/${parserId}/stop`, {}, { headers: this.getHeaders() }));
  }

  public deleteTelegramParser(parserId: string): Promise<HttpResponse<void>> {
    return lastValueFrom(this.http.delete<HttpResponse<void>>(`${this.telegramUrl}/parsers/${parserId}`, { headers: this.getHeaders() }));
  }

  public sendTelegramAuthCode(accountId: string): Promise<HttpResponse<TelegramSendCodeResult>> {
    return lastValueFrom(this.http.post<HttpResponse<TelegramSendCodeResult>>(`${this.telegramUrl}/accounts/${accountId}/auth/send-code`, {}, { headers: this.getHeaders() }));
  }

  public confirmTelegramAuthCode(accountId: string, code: string, password: string | null): Promise<HttpResponse<TelegramConfirmCodeResult>> {
    return lastValueFrom(this.http.post<HttpResponse<TelegramConfirmCodeResult>>(`${this.telegramUrl}/accounts/${accountId}/auth/confirm-code`, {
      code,
      password,
    }, { headers: this.getHeaders() }));
  }

  private getHeaders(): HttpHeaders {
    const token = this.accessService.getToken();
    return token ? new HttpHeaders({ Authorization: `Bearer ${token}` }) : new HttpHeaders();
  }
}
